/**
 * App-side wiring for `@heidi/agents`.
 *
 * Five things cross that the package's transports actually read, and only five
 * (a sixth, `getQueryClient`, is a declared crossing with no consumer yet and is
 * described after them). Four are reads of DEPLOYMENT or PLATFORM
 * state that the moved transports perform per request; three used to live in
 * `src/features/agent-harness/{url,stream/stream-http}.ts` — the two modules
 * that held that root's UI cone app-side — and the fourth in
 * `src/features/voice-assistant/client/voiceSocket.ts`. The fifth reads SESSION
 * state: which workspace the signed-in user is in.
 *
 * - `resolveApiOrigin` — `getDomains().baseV2Url`, the region- and
 *   environment-resolved ml-scribe v2 origin. The package strips trailing
 *   slashes itself, exactly as `agentHarnessUrl` did, so what crosses is the
 *   raw helper output rather than a normalised one.
 * - `resolveWebsocketOrigin` — `getDomains().websocketV1Url`, for the voice
 *   socket. A SECOND port rather than a reuse of the one above, because the two
 *   are measurably different values from different resolvers: `baseV2Url` is
 *   `https://<host>/api/v2/ml-scribe/` via `getUrlFromConfig`, and
 *   `websocketV1Url` is a bare `wss://<host>` via `getWebsocketUrlFromConfig`.
 *   The socket appends its own `/api/v2/ml-scribe/voice/realtime`, so bending
 *   the existing port to fit would have produced a doubled path on the wrong
 *   scheme.
 * - `getClientHeaders` — the `X-Client-Id` / `X-Client-Version` pair (reads
 *   `isTauri()` and the built app version) merged with the
 *   `Heidi-Workspace-Id` header (these raw SSE transports bypass the axios
 *   interceptor that stamps it everywhere else), with the on-prem
 *   optional-metadata omission applied on top (a no-op outside the on-prem
 *   desktop runtime).
 * - `getAuthTokenSync` — the synchronous token read. `undefined` is a real
 *   answer here (auth cache suppressed) and is passed through unchanged; the
 *   package's header factory omits `Authorization` for both `null` and
 *   `undefined` rather than stamping `Bearer undefined`.
 * - `getWorkspaceId` — the active workspace id, from the
 *   `@heidi/workspace/data` door. The voice socket sends it on the realtime
 *   handshake as `?workspace_id=`, because a browser cannot set a header on a
 *   WebSocket upgrade. Deliberately the SAME getter the `Heidi-Workspace-Id`
 *   header above stamps from, so the transports cannot disagree about which
 *   workspace a user is in.
 *
 * Every one is a getter and not a value: all five change while the app runs,
 * and none may be read at import time. The user-wide event transport already
 * supplies the first two of these under the same names through
 * `appUserEventsPorts` in `src/composition/hosts/agents/agent-events/agent-events-client.ts`;
 * that delegate is left alone rather than folded in here, because it owns a
 * fourth port (`isOffline`) that this seam does not.
 *
 * ONE MORE CROSSES BY RULING 65. It is a reactive IDENTITY read, a named hook
 * so the rules of hooks apply where an inline arrow would read as a callback,
 * and it crosses as the RESOLVED verdict rather than as the thing that
 * resolved it. Authentication and the overlay token-sync read live on
 * `@heidi/auth/state` now, wired once from the app seam.
 *
 * - `useIsIdentityHydrated` — `!!useUser().data`, the INC-215 boot-window
 *   proof. The Inbox reads stay closed until the user query resolves, so a
 *   stale boot-window token cannot fire them. `useUser` lives behind
 *   `@heidi/workspace/data`, which agents `/data` may not import — a foreign
 *   domain's non-`/contracts` door — so the verdict crosses here instead.
 *   READ OFF THAT DOOR AND NOT OFF `@/hooks/query/useQueryUser`, although the
 *   app path is a pure re-export of the same hook and the deleted Inbox
 *   delegate used it: this module is imported at `_app` MODULE SCOPE, and the
 *   app path's import graph reaches `src/store` through
 *   `useQueryKindeProperties` -> `scribeAPI` -> `getAuthTokenForHttpClient`.
 *   The door is already in this file's cone for `getWorkspaceId`, so the same
 *   function arrives with no new static dependency at all.
 *
 * `workHomeModeSettings` is the `getResolvedSettings` / `patchSettings` pair
 * from `@/pages/api/scribeAPIV2` that the Work home mode hooks call. It is bound
 * once here, so the Work route, the in-session composer and the note overlay
 * share one object and none of them imports the hub. `_app` already loads that
 * module through `workspace-data-wiring`, so the import adds nothing to its cone.
 *
 * WHAT IS NOT HERE. THE QUERY CLIENT DOES NOT CROSS AT ALL, in either shape,
 * and the getter that used to be wired here is gone. It never
 * crossed as the `queryClient: QueryClient` INSTANCE five other packages carry,
 * because every moved hook calls `useQueryClient()` in its own body as it did
 * app-side, and the three legs that still hold the app client hand it to a
 * callee that takes a `QueryClient` explicitly — pointing those at a config
 * member would re-introduce the singleton hold their shape avoids. It crossed
 * as a GETTER instead, wired unconditionally for a single named future adopter:
 * `src/features/tool-approvals/resolve-overlay-approval.ts`, a module-scope
 * resolver factory with zero production importers. That module was deleted
 * rather than moved, so the crossing had no adopter left, and a declared
 * crossing nothing reads is the shape the next module reaches for instead of
 * taking its `QueryClient` as a parameter. A package module that genuinely
 * needs a cache handle outside a render re-declares the member, required, in
 * the PR that has the reader. Still no flag — the Inbox gate is behind the door
 * now and reads `useIsProjectsEnabled` from `packages/agents/src/flags.ts`
 * relatively under ruling 36, so nothing about the flag crosses this seam — and
 * still no reactive `useAuthToken`: the gate reads authentication from
 * `@heidi/auth/state`, and the one verdict that still crosses here is identity
 * hydration. An input nobody consumes is surface with
 * nothing behind it.
 *
 * SAFE TO IMPORT STATICALLY, unlike the dictation seam beside it. That one
 * hands over a LOADER because its cone reaches the Rematch store, an async
 * module under rspack, and pulling it at `_app` module scope produced a TDZ
 * `ReferenceError` during "Collecting page data" on the integrations carve.
 * The ports below reach `getDomains` -> `getRegion` + the endpoints helpers,
 * `clientIdentityHeaders` -> `isTauri` + `appVersion`, the sync token helper,
 * and `@heidi/workspace/data` -> the jotai default store — all of which `_app`
 * already pulls at module scope through `workspace-data-wiring` and the http
 * client, so nothing new joins that cone. `@/utils/reactQueryClient` is in the
 * same position: `_app` builds the client it exports before it renders the
 * provider. `@/hooks/useIsAuthenticated` is too — `workspace-data-wiring`
 * imports `useAuthToken` from it.
 *
 * A SECOND SEAM IS CONFIGURED HERE, and it is deliberately not a widening of
 * the first. `configureVoiceBuildMode` carries the two build-mode booleans the
 * voice surfaces read — nothing region-resolved, nothing per request — and the
 * package defaults both to the production answer rather than throwing, because
 * the question they answer is "may this dev-only affordance exist". Keeping
 * them off `AgentsDataConfig` is what lets that default stand: every member of
 * that config is required-by-throw, and a build mode has a safe answer.
 *
 * Imported at module scope in `_app.tsx`.
 */
import { evidenceSidebarOpenAtom } from '@/atoms/evidenceSidebarAtoms';
import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import { resolveSurfaceRunAttribution } from '@/composition/hosts/evidence/in-session-surface/resolveSurfaceRunAttribution';
import {
  getIsInSessionSurfaceEnabled,
  IS_SLIDE_BUILDER_ENABLED,
  useIsSessionRoutineRunsEnabled,
  useIsWorkflowsEnabled,
  useIsVoiceDictationModeEnabled as useVoiceDictationModeFlag,
} from '@/features/flags';
import { useIsProjectsEnabled } from '@/features/flags/heidi-work-umbrella';
import { getDomains } from '@/helpers/getDomains';
import { GET_CHATS } from '@/hooks/query/useQueryChats';
import { GET_SAVED_CHATS } from '@/hooks/query/useQuerySavedChats';
import {
  trackWorkflowActioned,
  trackWorkflowRunClicked,
} from '@/models/analytics/features/workflowAnalytics';
import { getResolvedSettings, patchSettings } from '@/pages/api/scribeAPIV2';
import * as heidiReach from '@/utils/heidiReach';
import { getXClientHeaders } from '@/utils/http/clientIdentityHeaders';
import { getAuthTokenForHttpClient } from '@/utils/http/getAuthTokenForHttpClient';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { omitOnPremOptionalMetadataHeaders } from '@/utils/http/onPremOptionalMetadataHeaders';
import * as queryClientModule from '@/utils/reactQueryClient';
import {
  configureAgentsData,
  configureVoiceAppHost,
  configureVoiceBuildMode,
} from '@heidi/agents/data';
import { invalidateCreditAllowance } from '@heidi/commerce/data';
import { clearLibraryDocumentPreview } from '@heidi/evidence';
import { bridgeErrorKind } from '@heidi/plugin-core';
import { rpaBridge } from '@heidi/plugin-rpa';
import { getWorkspaceId, getWorkspaceIdHeader, useUser } from '@heidi/workspace/data';
import { getDefaultStore, useAtomValue } from 'jotai';
import Router, { useRouter } from 'next/router';

/** Deployments where a full voice session dump may be kept and logged. */
const DEBUG_DUMP_ENVIRONMENTS = new Set(['localhost', 'development', 'azure_dev']);

/**
 * `!!useUser().data` — has the signed-in identity finished hydrating. A named
 * hook, and a hook rather than a getter because the answer flips from false
 * to true during every boot.
 */
const useIsIdentityHydrated = () => !!useUser().data;

configureAgentsData({
  resolveApiOrigin: () => getDomains().baseV2Url,
  resolveWebsocketOrigin: () => getDomains().websocketV1Url,
  getClientHeaders: () =>
    omitOnPremOptionalMetadataHeaders({
      ...getXClientHeaders(),
      ...getWorkspaceIdHeader(),
    }),
  getAuthTokenSync: getAuthTokenForHttpClientSync,
  getAuthToken: getAuthTokenForHttpClient,
  getWorkspaceId,
  // Identity hydration stays here: `useUser` is a foreign domain's door.
  useIsIdentityHydrated,
  // The Heidi Work verdict (PostHog OR Flagsmith) for the Inbox gate.
  useIsHeidiWorkEnabled: useIsProjectsEnabled,
  // The settings pair the Work home mode hooks read and write through.
  workHomeModeSettings: { getResolvedSettings, patchSettings },
  // Pages that never mount ScribeLayout still call the composer reset and the
  // capability probe. The chat-list keys are imported, not re-declared.
  clearLibraryDocumentPreview,
  desktopClientCapabilities: {
    isAvailable: () => rpaBridge.isAvailable(),
    getCapabilities: () => rpaBridge.getCapabilities(),
    errorKind: bridgeErrorKind,
  },
  chatListQueryKeys: { chats: GET_CHATS, savedChats: GET_SAVED_CHATS },
  useIsWorkflowsEnabled: () => useIsWorkflowsEnabled(),
  useIsSessionRoutineRunsEnabled: () => useIsSessionRoutineRunsEnabled(),
  routinesAnalytics: {
    onRoutineActioned: (props) => trackWorkflowActioned(props),
    onRunClicked: (props) => {
      void trackWorkflowRunClicked(props);
    },
  },
  resolveWorkflowRunRequestExtras: (queryClient, run) =>
    import('@/app-shell/rpa/desktop-target/resolveWorkflowRunRequestExtras').then(
      (module) => module.resolveWorkflowRunRequestExtras(queryClient, run)
    ),
  invalidateCreditAllowance: () => invalidateCreditAllowance(),
  // The agent-event ui-tools read these per call, so a flag flip is seen at once.
  isSlideBuilderEnabled: () => IS_SLIDE_BUILDER_ENABLED(),
  isInSessionSurfaceEnabled: () => getIsInSessionSurfaceEnabled(),
  resolveSurfaceRunAttribution: (runs) => resolveSurfaceRunAttribution(runs),
});

configureVoiceBuildMode({
  isDevBuild: process.env.NODE_ENV !== 'production',
  isDebugDumpEnvironment:
    DEBUG_DUMP_ENVIRONMENTS.has(process.env.NEXT_PUBLIC_ENVIRONMENT ?? '') ||
    process.env.NODE_ENV === 'development',
});

const useVoicePathname = () => useRouter().pathname;

const useIsEvidenceSidebarOpen = () => useAtomValue(evidenceSidebarOpenAtom);

const useIsVisitRecordingActive = () => Boolean(useAtomValue(activeSessionIdAtom));

const useIsVoiceDictationModeEnabled = () => useVoiceDictationModeFlag();

configureVoiceAppHost({
  subscribeRouteChange: (listener) => {
    Router.events.on('routeChangeComplete', listener);
    return () => {
      Router.events.off('routeChangeComplete', listener);
    };
  },
  readRoute: () => ({
    pathname: Router.pathname,
    asPath: Router.asPath,
    query: Router.query,
  }),
  usePathname: useVoicePathname,
  getQueryClient: () => queryClientModule.reactQueryClient,
  openEvidenceSidebar: () => {
    getDefaultStore().set(evidenceSidebarOpenAtom, true);
  },
  useIsEvidenceSidebarOpen,
  isVisitRecordingActive: () => Boolean(getDefaultStore().get(activeSessionIdAtom)),
  useIsVisitRecordingActive,
  useIsVoiceDictationModeEnabled,
  isProductFeedbackAvailable: () => heidiReach.isProductFeedbackAvailable,
  // Lazy: the submit path pulls in the `@heidi/agents` UI door, which this
  // module is evaluated ahead of.
  submitVoiceWorkTurn: async (turn) => {
    const { submitVoiceWorkTurn } = await import('@/app-shell/rpa/voice-work-turn');
    return submitVoiceWorkTurn(turn);
  },
  // Lazy for the same reason: the Kinde query module reaches `src/store`.
  readAuthenticatedRegion: async () => {
    const { getCachedKindeRegion } =
      await import('@/hooks/query/useQueryKindeProperties');
    return getCachedKindeRegion();
  },
});
